import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Download, X } from "lucide-react";
import { api } from "@/lib/api";

const TIER_LABELS = {
  lease_mp3: "MP3 Lease",
  lease_wav: "WAV Lease",
  lease_trackout: "Trackout Lease",
  exclusive: "Exclusive Rights",
};

const DownloadsModal = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const { data: tiers = [] } = useQuery({
    queryKey: ["tiers"],
    queryFn: async () => (await api.get("/license-tiers")).data,
    enabled: open,
  });

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-downloads", h);
    return () => window.removeEventListener("open-downloads", h);
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders?email=${encodeURIComponent(email.trim())}`);
      setOrders(data);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const tierName = (key) => tiers.find((t) => t.lookup_key === key)?.name || TIER_LABELS[key] || key;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
          onClick={() => setOpen(false)}
          data-testid="downloads-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[#0B0B10] border border-white/15 p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Download size={18} className="text-[#D65BB4]" />
                <h3 className="font-display font-bold uppercase text-sm tracking-widest">My Downloads</h3>
              </div>
              <button
                data-testid="downloads-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-white/50 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <label htmlFor="downloads-email" className="text-xs uppercase tracking-[0.3em] text-white/50 block mb-3">
              Email you checked out with
            </label>
            <div className="flex gap-3">
              <input
                id="downloads-email"
                data-testid="downloads-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-[#111116] border-0 border-b border-white/20 focus:border-[#D65BB4] px-4 py-3 text-sm outline-none transition-colors placeholder:text-white/30"
              />
              <button
                data-testid="downloads-search-btn"
                onClick={search}
                disabled={loading || !email.trim()}
                className="bg-[#D65BB4] px-6 text-xs uppercase tracking-widest font-bold hover:shadow-[0_0_32px_rgba(214,91,180,0.5)] transition-shadow disabled:opacity-40"
              >
                {loading ? "…" : "Search"}
              </button>
            </div>

            <div className="mt-8 flex flex-col gap-3" data-testid="downloads-results">
              {orders && orders.length === 0 && (
                <p className="text-sm text-white/50">
                  No purchases found for that email. Double-check the address you used at checkout.
                </p>
              )}
              {orders &&
                orders.map((o) => (
                  <div
                    key={o.session_id}
                    data-testid={`downloads-order-${o.session_id.slice(-8)}`}
                    className="flex items-center justify-between border border-white/10 bg-white/[0.03] px-5 py-4"
                  >
                    <div>
                      <div className="font-display font-bold uppercase text-sm">{o.beat_title || "Beat license"}</div>
                      <div className="text-xs text-white/50 mt-1">
                        {tierName(o.tier)} · {new Date(o.purchased_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="font-display font-bold text-[#4DBDBE]">
                      ${((o.amount || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DownloadsModal;
